import { useCallback, useState, useEffect } from "react";
import { Card, Row, Col, Spin, Tag, Button, Typography } from "antd";
import { customBaseUrl, httpGet, httpPost } from "../../../../store/http";
import Swal from "sweetalert2";
import { hideLoader, showLoader } from "../../../../utils/loader";
import { NotificationManager } from "react-notifications";

const { Text } = Typography;

export const CustomerActivities = ({ id, identifier, activeTab }) => {
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpCounts, setOtpCounts] = useState({
    LOGIN: { attempts: 0, maxAttempts: 5, blocked: false },
    PASSWORD_RESET: { attempts: 0, maxAttempts: 5, blocked: false },
    PIN_RESET: { attempts: 0, maxAttempts: 5, blocked: false },
    REGISTRATION: { attempts: 0, maxAttempts: 5, blocked: false },
    HMAC_REISSUE: { attempts: 0, maxAttempts: 5, blocked: false },
    DEVICE_CHANGE: { attempts: 0, maxAttempts: 5, blocked: false },
  });

  const fetchOtpAttempts = useCallback(async () => {
    if (!id) return;

    if (!identifier) return;

    setOtpLoading(true);
    const purposes = ["LOGIN", "PASSWORD_RESET", "PIN_RESET", "REGISTRATION", "HMAC_REISSUE", "DEVICE_CHANGE"];
    const counts = {};
    try {
      await Promise.all(
        purposes.map(async (purpose) => {
          try {
            const res = await httpGet(
              `${customBaseUrl.authUrl}/api/v1/admin/users/otp/attempts?identifier=${encodeURIComponent(identifier)}&purpose=${purpose}`,
            );
            if (res && res.success && res.data) {
              counts[purpose] = {
                attempts: res.data.attempts || 0,
                maxAttempts: res.data.maxAttempts || 5,
                blocked: res.data.blocked || false,
              };
            } else {
              counts[purpose] = { attempts: 0, maxAttempts: 5, blocked: false };
            }
          } catch (err) {
            console.error(`Error fetching OTP count for ${purpose}:`, err);
            counts[purpose] = { attempts: 0, maxAttempts: 5, blocked: false };
          }
        }),
      );
      setOtpCounts(counts);
    } catch (err) {
      console.error("Error in batch OTP fetch:", err);
    } finally {
      setOtpLoading(false);
    }
  }, [id, identifier]);

  const handleResetOtpAttempts = async (purpose) => {
    const result = await Swal.fire({
      title: "Reset OTP Attempts?",
      text: `Are you sure you want to reset ${purpose} OTP attempts for ${identifier}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, reset it!",
    });

    if (result.isConfirmed) {
      showLoader();
      try {
        const res = await httpPost(
          `${customBaseUrl.authUrl}/api/v1/admin/users/otp/reset-attempts?identifier=${encodeURIComponent(identifier)}&purpose=${purpose}`,
          {},
          false,
          null,
          false,
        );
        if (res && res.success) {
          NotificationManager.success(
            res.message || "OTP attempts reset successfully",
          );
          fetchOtpAttempts();
        } else {
          NotificationManager.error(
            res?.message || "Failed to reset OTP attempts",
          );
        }
      } catch (error) {
        NotificationManager.error(
          "An error occurred while resetting OTP attempts",
        );
      } finally {
        hideLoader();
      }
    }
  };

  const handleResetAllOtpAttempts = async () => {
    const result = await Swal.fire({
      title: "Reset ALL OTP Attempts?",
      text: `Are you sure you want to reset ALL OTP attempts for ${identifier}? This will clear blocks for all purposes (LOGIN, PASSWORD_RESET, etc.)`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, reset all!",
    });

    if (result.isConfirmed) {
      showLoader();
      try {
        const res = await httpPost(
          `${customBaseUrl.authUrl}/api/v1/admin/users/otp/reset-all-attempts?identifier=${encodeURIComponent(identifier)}`,
          {},
          false,
          null,
          false,
        );
        if (res && res.success) {
          NotificationManager.success(
            res.message || "All OTP attempts reset successfully",
          );
          fetchOtpAttempts();
        } else {
          NotificationManager.error(
            res?.message || "Failed to reset all OTP attempts",
          );
        }
      } catch (error) {
        NotificationManager.error(
          "An error occurred while resetting all OTP attempts",
        );
      } finally {
        hideLoader();
      }
    }
  };

  useEffect(() => {
    if (activeTab === "otp" || activeTab === "transactions") {
      fetchOtpAttempts();
    }
  }, [activeTab, fetchOtpAttempts]);

  if (otpLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          padding: "80px 0",
        }}
      >
        <Spin tip="Loading OTP status..." />
      </div>
    );
  }

  return (
    <Card
      title="Customer Activities"
      style={{
        borderRadius: 12,
        padding: 20,
      }}
    >
      <Row gutter={[16, 16]}>
        {Object.entries(otpCounts).map(([purpose, data]) => {
          const isDisabled = data.attempts === 0 && !data.blocked;

          return (
            <Col xs={24} sm={12} md={6} key={purpose}>
              <Card
                size="small"
                title={formatPurpose(purpose)}
                style={{ borderRadius: 8, padding: 12 }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Text strong style={{ fontSize: 16 }}>
                      {data.attempts} / {data.maxAttempts}
                    </Text>

                    {data.blocked && (
                      <Tag color="error" style={{ margin: 0 }}>
                        Blocked
                      </Tag>
                    )}
                  </div>

                  <Button
                    type="link"
                    danger
                    size="small"
                    style={{
                      padding: 0,
                      textAlign: "left",
                      width: "fit-content",
                      fontWeight: 500,
                    }}
                    onClick={() => handleResetOtpAttempts(purpose)}
                    disabled={isDisabled}
                  >
                    Reset Attempts
                  </Button>
                </div>
              </Card>
            </Col>
          );
        })}
      </Row>

      <div
        style={{
          marginTop: 24,
          paddingTop: 16,
          borderTop: "1px solid #f0f0f0",
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Button type="default" onClick={handleResetAllOtpAttempts}>
          Reset All OTP Attempts
        </Button>
      </div>
    </Card>
  );
};

const formatPurpose = (purpose) =>
  purpose
    ?.toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
